import { z } from 'zod'
import { Company, PaymentTermOption, paymentTermOptionsList } from './companyModel'
import { Contact, ContactSchema, Identifier } from './debtorModels'
import { LoanGateway } from './loanModels'
import { Currency } from './moneyModel'
import { Root } from './rootModel'
import { Attachment, AttachmentGateway } from './uploadModels'
import { Receivable } from './receivableModels'
import { nullableThenRequired } from '../utils/modelUtils'

export const invoiceOwners = ['company', 'user'] as const
export type InvoiceOwner = (typeof invoiceOwners)[number]

export const invoiceStatuses = ['OPEN', 'REPAID', 'PAST_DUE', 'CANCELED'] as const
export type InvoiceStatus = (typeof invoiceStatuses)[number]

export type InvoiceGateway = Root & {
    companyId: Company['id']
    attachments?: AttachmentGateway[]
    currency: Currency
    totalAmountIncludingTaxes: number
    totalAmountExcludingTaxes?: number
    invoiceDate: string // Date in ISO 8601 UTC format
    invoiceNumber: string
    debtorName: string
    debtorIdentifier: Identifier
    repaymentPeriod: number
    paymentTermType: PaymentTermOption
    dueDate: string // Date in ISO 8601 UTC format
    loans?: LoanGateway[]
    contacts?: Contact[]
    invoiceOwner?: InvoiceOwner
    debtorId?: string
    status: InvoiceStatus
    receivableIds?: Receivable['id'][]
}

export type Invoice = Omit<InvoiceGateway, 'attachments'> & {
    isEditable: boolean
    attachments?: Attachment[]
    attachmentCount?: number
}

export function isInvoice(doc: Invoice | Receivable | undefined): doc is Invoice {
    return doc !== undefined && 'invoiceDate' in doc
}

export type CreateInvoiceGateway = Pick<
    InvoiceGateway,
    | 'debtorName'
    | 'totalAmountIncludingTaxes'
    | 'totalAmountExcludingTaxes'
    | 'invoiceDate'
    | 'invoiceNumber'
    | 'debtorIdentifier'
    | 'repaymentPeriod'
    | 'paymentTermType'
    | 'currency'
> & {
    attachments: AttachmentGateway[] | undefined
    contacts: Contact[]
    invoiceOwner: InvoiceOwner | undefined
}

export type UpdateInvoice = Pick<
    Invoice,
    'debtorId' | 'invoiceNumber' | 'repaymentPeriod' | 'paymentTermType' | 'invoiceOwner'
> & {
    attachments: File[]
    invoiceDate?: string
    invoiceId?: Invoice['id']
    debtorName: Invoice['debtorName']
    totalAmountIncludingTaxes?: number
    totalAmountExcludingTaxes?: number
    debtorIdentifier: Invoice['debtorIdentifier']
    currency?: Currency
    contacts: Contact[]
    receivables?: Receivable['id'][]
}

export type UnlinkReceivable = {
    invoiceId: Invoice['id'] | undefined
    receivableIdToUnlink: Receivable['id'] | undefined
    currentReceivableIds: Receivable['id'][]
}

export type LinkReceivable = {
    invoiceId: Invoice['id'] | undefined
    receivableIdsToLink: Receivable['id'][] | undefined
    currentReceivableIds: Receivable['id'][]
}

export type UnlinkReceivablePayload = {
    receivableIds: Receivable['id'][]
}

export type LinkReceivablePayload = {
    receivableIds: Receivable['id'][]
}

export type UpdateAttachments<T extends { id: string; attachments?: Attachment[] }> = Pick<T, 'id' | 'attachments'> & {
    attachment: Attachment
}

export type UpdateInvoiceAttachments = UpdateAttachments<Invoice>

export type UpdateInvoiceGateway = Partial<
    Pick<
        InvoiceGateway,
        | 'totalAmountIncludingTaxes'
        | 'totalAmountExcludingTaxes'
        | 'invoiceDate'
        | 'invoiceNumber'
        | 'debtorName'
        | 'debtorIdentifier'
        | 'repaymentPeriod'
        | 'paymentTermType'
        | 'contacts'
        | 'debtorId'
    >
> & {
    attachments: AttachmentGateway[] | undefined
    currency?: Invoice['currency']
    invoiceOwner?: 'user' | 'company'
}

export type UpdateInvoiceFormAttributes = Pick<
    UpdateInvoice,
    | 'repaymentPeriod'
    | 'paymentTermType'
    | 'totalAmountIncludingTaxes'
    | 'totalAmountExcludingTaxes'
    | 'invoiceDate'
    | 'invoiceNumber'
    | 'debtorName'
    | 'debtorIdentifier'
    | 'currency'
    | 'contacts'
    | 'attachments'
> & {
    invoiceId: Invoice['id'] | undefined
}

export type DeleteInvoice = {
    invoiceId: Invoice['id']
}

export const CreateInvoiceFormSchema = z.object({
    invoiceNumber: z.string().min(1),
    invoiceDate: z.date(),
    totalAmountIncludingTaxes: z.number().min(0.01),
    totalAmountExcludingTaxes: z.number().min(0.01),
    invoiceOwner: z.enum(invoiceOwners).optional(),
    contacts: z.array(ContactSchema).min(1),
    attachments: z.array(z.instanceof(File)),
    paymentTermType: nullableThenRequired(z.enum(paymentTermOptionsList)),
    repaymentPeriod: nullableThenRequired(z.number()),
    debtorName: z.string().min(1)
})

export type CreateInvoiceForm = z.infer<typeof CreateInvoiceFormSchema>

export type CreateInvoice = {
    invoiceNumber: string
    invoiceDate: string
    currency: Currency
    totalAmountExcludingTaxes: number
    totalAmountIncludingTaxes: number
    attachments: File[]
    contacts: Contact[]
    repaymentPeriod: number
    paymentTermType: PaymentTermOption
    invoiceOwner: InvoiceOwner | undefined
    debtorName: string
    debtorIdentifier: Identifier
}
